import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ProductCard = ({ product, showQuickAdd = true }) => {
  const [isAdding, setIsAdding] = useState(false);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsAdding(true);
    
    // Get existing cart
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    
    // Check if product already exists
    const existingItemIndex = existingCart.findIndex(item => item.id === product.id);
    
    if (existingItemIndex > -1) {
      // Update quantity
      existingCart[existingItemIndex].quantity += 1;
    } else {
      // Add new item
      existingCart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: 1
      });
    }
    
    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    // Reset loading state
    setTimeout(() => {
      setIsAdding(false);
    }, 500);
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const getDiscountPercentage = () => {
    if (product.originalPrice && product.originalPrice > product.price) {
      return Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
    }
    return 0;
  };

  const discountPercentage = getDiscountPercentage();

  return (
    <div className="bg-surface rounded-card shadow-card hover:shadow-medium transition-smooth border border-border-light group">
      <Link to={`/product-detail-page?id=${product.id}`} className="block">
        <div className="relative overflow-hidden rounded-t-card">
          <Image
            src={product.image}
            alt={product.name}
            className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-smooth"
          />
          
          {/* Discount Badge */}
          {discountPercentage > 0 && (
            <div className="absolute top-2 left-2 bg-accent text-accent-foreground px-2 py-1 rounded-input text-xs font-data font-data-medium">
              {discountPercentage}% OFF
            </div>
          )}
          
          {/* Stock Status */}
          {product.stock <= 5 && product.stock > 0 && (
            <div className="absolute top-2 right-2 bg-warning text-warning-foreground px-2 py-1 rounded-input text-xs font-data font-data-medium">
              Only {product.stock} left
            </div>
          )}
          
          {product.stock === 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <span className="bg-error text-error-foreground px-3 py-1 rounded-input text-sm font-body-semibold">
                Out of Stock
              </span>
            </div>
          )}
        </div>
      </Link>

      <div className="p-4">
        <Link to={`/product-detail-page?id=${product.id}`}>
          <h3 className="font-body-semibold text-sm md:text-base text-text-primary mb-2 line-clamp-2 group-hover:text-primary transition-smooth">
            {product.name}
          </h3>
        </Link>
        
        {product.brand && (
          <p className="font-caption text-xs text-text-secondary mb-2">
            {product.brand}
          </p>
        )}
        
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="font-data font-data-medium text-lg text-primary">
              {formatPrice(product.price)}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="font-data text-sm text-text-secondary line-through">
                {formatPrice(product.originalPrice)}
              </span>
            )}
          </div>
          
          {product.rating && (
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={14} className="text-yellow-500 fill-current" />
              <span className="font-data text-xs text-text-secondary">
                {product.rating}
              </span>
            </div>
          )}
        </div>
        
        {product.unit && (
          <p className="font-caption text-xs text-text-secondary mb-3">
            Per {product.unit}
          </p>
        )}
        
        {showQuickAdd && (
          <Button
            variant={product.stock === 0 ? "outline" : "primary"}
            size="sm"
            fullWidth
            onClick={handleAddToCart}
            disabled={product.stock === 0 || isAdding}
            loading={isAdding}
            iconName={product.stock === 0 ? "AlertCircle" : "Plus"}
            iconSize={16}
          >
            {product.stock === 0 ? "Out of Stock" : isAdding ? "Adding..." : "Add to Cart"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;